import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ScanSearchIcon, UsersIcon, TargetIcon } from 'lucide-react';
interface Capability {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  color: string;
}
const CAPABILITIES: Capability[] = [{
  id: 'pattern',
  title: 'Pattern Recognition',
  description: 'Analyzes thousands of successful game loops to identify winning mechanics and structural patterns that drive engagement.',
  icon: <ScanSearchIcon className="w-7 h-7" aria-hidden="true" data-id="element-195" />,
  color: '#F5A623'
}, {
  id: 'behavioral',
  title: 'Behavioral Simulation',
  description: 'Models player behavior across demographics to predict engagement, churn points, and session depth before a single line of code is written.',
  icon: <UsersIcon className="w-7 h-7" aria-hidden="true" data-id="element-196" />,
  color: '#4FC3F7'
}, {
  id: 'reward',
  title: 'Reward Optimization',
  description: 'Fine-tunes reward schedules and progression curves with reinforcement learning to maximize long-term player retention.',
  icon: <TargetIcon className="w-7 h-7" aria-hidden="true" data-id="element-197" />,
  color: '#81C784'
}];
export function WhatWeBuild() {
  const [activeId, setActiveId] = useState<string | null>(null);
  return <section id="solution" className="relative w-full py-32 lg:py-40 overflow-hidden bg-gradient-to-b from-midnight-light to-midnight-deep" aria-labelledby="solution-heading" data-id="element-198">
      {/* Background accents */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-amber/[0.02] blur-[120px]" data-id="element-199" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8" data-id="element-200">
        {/* Section header */}
        <motion.div className="text-center mb-20" initial={{
        opacity: 0,
        y: 40
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-201">
          <p className="font-sora text-amber text-sm font-semibold tracking-[0.2em] uppercase mb-4" data-id="element-202">
            Our Technology
          </p>
          <h2 id="solution-heading" className="font-sora text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6" data-id="element-203">
            What We Build
          </h2>
          <p className="font-inter text-lg text-text-secondary max-w-2xl mx-auto" data-id="element-204">
            Three AI engines working in concert to transform how game loops are
            designed, tested, and optimized.
          </p>
        </motion.div>

        {/* Capability nodes */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8" data-id="element-205">
          {CAPABILITIES.map((cap, i) => {
          const isActive = activeId === cap.id;
          return <motion.div key={cap.id} className="relative" initial={{
            opacity: 0,
            y: 50
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-80px'
          }} transition={{
            duration: 0.7,
            delay: i * 0.15,
            ease: [0.22, 1, 0.36, 1]
          }} data-id="element-206">
                <motion.button className={`w-full text-left p-8 lg:p-10 rounded-2xl border transition-colors duration-500 cursor-pointer ${isActive ? 'bg-midnight-lighter/70 border-amber/30' : 'bg-midnight-lighter/30 border-white/5 hover:border-white/10'}`} onClick={() => setActiveId(isActive ? null : cap.id)} onMouseEnter={() => setActiveId(cap.id)} onMouseLeave={() => setActiveId(null)} whileHover={{
              scale: 1.02
            }} transition={{
              type: 'spring',
              stiffness: 300,
              damping: 25
            }} aria-expanded={isActive} aria-label={`${cap.title} — click to ${isActive ? 'collapse' : 'expand'} details`} data-id="element-207">
                  {/* Glow effect when active */}
                  <AnimatePresence data-id="element-208">
                    {isActive && <motion.div className="absolute inset-0 rounded-2xl" style={{
                  boxShadow: `0 0 40px ${cap.color}15, inset 0 1px 0 ${cap.color}20`
                }} initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} exit={{
                  opacity: 0
                }} transition={{
                  duration: 0.3
                }} data-id="element-209" />}
                  </AnimatePresence>

                  {/* Icon */}
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl mb-6 transition-colors duration-300" style={{
                backgroundColor: `${cap.color}15`,
                color: cap.color
              }} data-id="element-210">
                    {cap.icon}
                  </div>

                  {/* Title */}
                  <h3 className="font-sora text-xl lg:text-2xl font-semibold text-white mb-3" data-id="element-211">
                    {cap.title}
                  </h3>

                  {/* Animated connection line */}
                  <motion.div className="h-px mb-4" style={{
                backgroundColor: cap.color
              }} initial={{
                width: '40px',
                opacity: 0.3
              }} animate={isActive ? {
                width: '100%',
                opacity: 0.4
              } : {
                width: '40px',
                opacity: 0.3
              }} transition={{
                duration: 0.5,
                ease: [0.22, 1, 0.36, 1]
              }} data-id="element-212" />

                  {/* Description — always present but animated */}
                  <AnimatePresence mode="wait" data-id="element-213">
                    {isActive ? <motion.p key="full" className="font-inter text-text-secondary leading-relaxed" initial={{
                  opacity: 0,
                  height: 0
                }} animate={{
                  opacity: 1,
                  height: 'auto'
                }} exit={{
                  opacity: 0,
                  height: 0
                }} transition={{
                  duration: 0.4,
                  ease: [0.22, 1, 0.36, 1]
                }} data-id="element-214">
                        {cap.description}
                      </motion.p> : <motion.p key="teaser" className="font-inter text-text-muted text-sm" initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} exit={{
                  opacity: 0
                }} transition={{
                  duration: 0.3
                }} data-id="element-215">
                        Hover or tap to explore →
                      </motion.p>}
                  </AnimatePresence>
                </motion.button>

                {/* Connecting lines between cards (desktop only) */}
                {i < CAPABILITIES.length - 1 && <div className="hidden lg:block absolute top-1/2 -right-4 w-8 h-px" data-id="element-216">
                    <div className="w-full h-px bg-gradient-to-r from-white/10 to-white/5" data-id="element-217" />
                    <motion.div className="absolute top-0 left-0 h-px bg-amber/30" animate={{
                width: ['0%', '100%', '0%']
              }} transition={{
                duration: 3,
                repeat: Infinity,
                delay: i * 0.5
              }} data-id="element-218" />
                  </div>}
              </motion.div>;
        })}
        </div>
      </div>
    </section>;
}